import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  fetchMembers,
  fetchGroups,
} from "../features/members/slice-extensions/members-slice-extension";
import {
  selectMembers,
  selectGroups,
  selectMembersStatus,
  selectGroupsStatus,
  selectSelectedTab,
  selectMembersInitializationStatus,
  selectGroupsInitializationStatus,
  setSelectedTab,
} from "../features/members/members-slice";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import Tabs from "../components/Tabs";
import { Member } from "../features/members/models/Member";
import { Group } from "../features/members/models/Group";

/**
 * Generates initials from a first and last name.
 * Returns up to two uppercase characters.
 */
const getInitials = (firstName: string, lastName: string): string => {
  const first = firstName.trim().charAt(0).toUpperCase();
  const last = lastName.trim().charAt(0).toUpperCase();
  return `${first}${last}`;
};

/**
 * Generates a short abbreviation for a group name.
 * Takes the first letter of each word (up to 2 letters).
 */
const getGroupAbbreviation = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  }
  return name.trim().substring(0, 2).toUpperCase();
};

/**
 * Simple hash function for a string, returning a positive integer.
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return Math.abs(hash);
};

/** Member avatar colors — warm/teal tones */
const MEMBER_COLORS = [
  "#367D7D", // teal (original)
  "#2E6B6B", // dark teal
  "#3D8F8F", // light teal
  "#2A7A5E", // sea green
  "#4A8C6F", // sage
  "#3B7A9E", // steel blue
  "#2D6E8A", // ocean
  "#458B74", // jade
];

/** Group avatar colors — purple tones from project palette */
const GROUP_COLORS = [
  "#6317CF", // primary-40
  "#462673", // primary-30
  "#6C0DF2", // primary-50
  "#8833FF", // primary-60
  "#9966FF", // primary-70
  "#993300", // secondary-30
  "#C2570A", // secondary-40
  "#0F8A80", // tertiary-30
];

const getMemberColor = (firstName: string, lastName: string): string => {
  return MEMBER_COLORS[hashString(firstName + lastName) % MEMBER_COLORS.length];
};

const getGroupColor = (name: string): string => {
  return GROUP_COLORS[hashString(name) % GROUP_COLORS.length];
};

const MembersListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const members = useAppSelector(selectMembers);
  const groups = useAppSelector(selectGroups);
  const membersStatus = useAppSelector(selectMembersStatus);
  const groupsStatus = useAppSelector(selectGroupsStatus);
  const selectedTab = useAppSelector(selectSelectedTab);
  const membersInitializationStatus = useAppSelector(
    selectMembersInitializationStatus
  );
  const groupsInitializationStatus = useAppSelector(
    selectGroupsInitializationStatus
  );

  // Tooltip state for group avatars
  const [groupTooltip, setGroupTooltip] = useState<{
    groupId: string;
    position: 'top' | 'bottom';
    top: number;
    left: number;
  } | null>(null);

  const showGroupTooltip = (groupId: string, el: HTMLDivElement) => {
    const rect = el.getBoundingClientRect();
    const pos = rect.top < 100 ? 'bottom' : 'top';
    const left = rect.left + rect.width / 2;
    const top = pos === 'top' ? rect.top : rect.bottom;
    setGroupTooltip({ groupId, position: pos, top, left });
  };

  const hideGroupTooltip = () => {
    setGroupTooltip(null);
  };

  // Tab configuration
  const tabConfig = [
    { label: t("members.tabs.all"), value: "all" as const },
    { label: t("members.tabs.individuals"), value: "individuals" as const },
    { label: t("members.tabs.groups"), value: "groups" as const },
  ];

  const selectedTabIndex = tabConfig.findIndex(
    (tab) => tab.value === selectedTab
  );

  const handleTabSelect = (index: number) => {
    const tabValue = tabConfig[index]?.value;
    if (tabValue) {
      dispatch(setSelectedTab(tabValue));
    }
  };

  // Sync tab selection with URL
  useEffect(() => {
    if (location.pathname === ROUTES.GROUPS) {
      dispatch(setSelectedTab("groups"));
    } else if (location.pathname === ROUTES.MEMBERS) {
      dispatch(setSelectedTab("all"));
    }
  }, [location.pathname, dispatch]);

  // Fetch members and groups on every mount to keep data fresh
  useEffect(() => {
    dispatch(fetchMembers());
    dispatch(fetchGroups());
  }, [dispatch]);

  const handleMemberClick = (memberId: string) => {
    navigate(ROUTES.MEMBER_DETAILS.replace(":id", memberId));
  };

  const handleGroupClick = (groupId: string) => {
    navigate(ROUTES.GROUP_DETAILS.replace(":id", groupId));
  };

  const handleCreateMember = () => {
    navigate(ROUTES.CREATE_MEMBER);
  };

  const handleCreateGroup = () => {
    navigate(ROUTES.CREATE_GROUP);
  };

  // Determine if loading
  const isLoading =
    (membersStatus === LoadingStatus.Loading &&
      (membersInitializationStatus === "initializing" ||
        membersInitializationStatus === "initialized")) ||
    (groupsStatus === LoadingStatus.Loading &&
      (groupsInitializationStatus === "initializing" ||
        groupsInitializationStatus === "initialized"));

  // Build the filtered list based on selected tab
  const getFilteredItems = (): Array<
    { type: "member"; data: Member } | { type: "group"; data: Group }
  > => {
    if (selectedTab === "individuals") {
      return members.map((m) => ({ type: "member" as const, data: m }));
    }
    if (selectedTab === "groups") {
      return groups.map((g) => ({ type: "group" as const, data: g }));
    }
    // "all" tab: interleave members and groups - members first, then groups
    const memberItems = members.map((m) => ({
      type: "member" as const,
      data: m,
    }));
    const groupItems = groups.map((g) => ({
      type: "group" as const,
      data: g,
    }));
    return [...memberItems, ...groupItems];
  };

  const filteredItems = getFilteredItems();

  // Loading state
  if (isLoading) {
    return (
      <section>
        <div className="flex h-full gap-4">
          <LeftMenu />
          <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
            <div className="lg:max-w-[694px]">
              <Breadcrumbs />
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <p className="text-lg text-grey-30 dark:text-grey-70">
                    {t("members.loading")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side */}
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[694px]">
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              {/* Title and Create Button */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h1>{t("members.title")}</h1>
                <button
                  className="btn btn-primary"
                  onClick={handleCreateMember}
                >
                  <svg
                    width={24}
                    height={24}
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      className="fill-current"
                      d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z"
                    />
                  </svg>
                  <span>{t("members.newMember")}</span>
                </button>
              </div>

              {/* Tabs */}
              <div className="space-y-6 lg:space-y-4">
                <div className="space-y-2">
                  <Tabs
                    tabs={tabConfig.map((tab) => tab.label)}
                    selectedIndex={selectedTabIndex}
                    onSelect={handleTabSelect}
                    headerClassName="grid-cols-3"
                    idPrefix="members-tab"
                  />
                </div>
              </div>

              {/* Error State */}
              {(membersStatus === LoadingStatus.Failed ||
                groupsStatus === LoadingStatus.Failed) && (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
                  <p>{t("common.errorLoadingData")}</p>
                </div>
              )}

              {/* Members and Groups List */}
              <div className="space-y-2 lg:space-y-4">
                {filteredItems.length === 0 &&
                  membersStatus === LoadingStatus.Idle &&
                  groupsStatus === LoadingStatus.Idle && (
                    <div className="rounded-lg bg-grey-95 dark:bg-primary-20 p-8 text-center">
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-grey-85 dark:bg-grey-30">
                        <svg
                          width={32}
                          height={32}
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          className="text-grey-50 dark:text-grey-70"
                        >
                          <path
                            d="M16.6699 13.13C18.0399 14.06 18.9999 15.32 18.9999 17V20H22.9999V17C22.9999 14.82 19.4299 13.53 16.6699 13.13Z"
                            fill="currentColor"
                          />
                          <path
                            d="M14.9999 12C17.2099 12 18.9999 10.21 18.9999 8C18.9999 5.79 17.2099 4 14.9999 4C14.5299 4 14.0899 4.1 13.6699 4.24C14.4999 5.27 14.9999 6.58 14.9999 8C14.9999 9.42 14.4999 10.73 13.6699 11.76C14.0899 11.9 14.5299 12 14.9999 12Z"
                            fill="currentColor"
                          />
                          <path
                            d="M8.99994 12C11.2099 12 12.9999 10.21 12.9999 8C12.9999 5.79 11.2099 4 8.99994 4C6.78994 4 4.99994 5.79 4.99994 8C4.99994 10.21 6.78994 12 8.99994 12ZM8.99994 6C10.0999 6 10.9999 6.9 10.9999 8C10.9999 9.1 10.0999 10 8.99994 10C7.89994 10 6.99994 9.1 6.99994 8C6.99994 6.9 7.89994 6 8.99994 6Z"
                            fill="currentColor"
                          />
                          <path
                            d="M8.99994 13C6.32994 13 0.999939 14.34 0.999939 17V20H16.9999V17C16.9999 14.34 11.6699 13 8.99994 13ZM14.9999 18H2.99994V17.01C3.19994 16.29 6.29994 15 8.99994 15C11.6999 15 14.7999 16.29 14.9999 17V18Z"
                            fill="currentColor"
                          />
                        </svg>
                      </div>
                      <h3 className="mb-2 text-lg font-medium text-grey-20 dark:text-white">
                        {selectedTab === "groups"
                          ? t("groups.emptyState.title")
                          : t("members.emptyState.title")}
                      </h3>
                      <p className="mb-4 text-grey-50 dark:text-grey-70">
                        {selectedTab === "groups"
                          ? t("groups.emptyState.description")
                          : t("members.emptyState.description")}
                      </p>
                      {selectedTab === "groups" && (
                        <button
                          className="btn btn-primary"
                          onClick={handleCreateGroup}
                        >
                          <svg
                            width={24}
                            height={24}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              className="fill-current"
                              d="M12 6C12.5523 6 13 6.44772 13 7V11H17C17.5523 11 18 11.4477 18 12C18 12.5523 17.5523 13 17 13H13V17C13 17.5523 12.5523 18 12 18C11.4477 18 11 17.5523 11 17V13H7C6.44772 13 6 12.5523 6 12C6 11.4477 6.44772 11 7 11H11V7C11 6.44772 11.4477 6 12 6Z"
                            />
                          </svg>
                          <span>{t("groups.newGroup")}</span>
                        </button>
                      )}
                    </div>
                  )}

                {/* Render each item as a card */}
                {filteredItems.map((item) => {
                  if (item.type === "member") {
                    const member = item.data as Member;
                    const initials = getInitials(
                      member.firstName,
                      member.lastName
                    );
                    return (
                      <div
                        key={`member-${member.id}`}
                        className="with-border flex cursor-pointer overflow-hidden rounded-2xl transition-colors hover:bg-grey-95 dark:hover:bg-primary-20"
                        onClick={() => handleMemberClick(member.id)}
                      >
                        <div className="flex w-full items-center gap-3 px-4 py-3">
                          {/* Avatar */}
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
                            style={{ backgroundColor: getMemberColor(member.firstName, member.lastName) }}
                            title={`${member.firstName} ${member.lastName}`}
                          >
                            {initials}
                          </div>

                          {/* Info */}
                          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <p className="truncate font-semibold text-grey-10 dark:text-white cursor-pointer hover:underline">
                              {member.firstName} {member.lastName}
                            </p>
                            {member.email && (
                              <p className="truncate text-sm text-grey-50 dark:text-grey-70">
                                {member.email}
                              </p>
                            )}
                            {member.groups.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {member.groups.map((group) => (
                                  <span
                                    key={group.id}
                                    className="inline-flex items-center rounded-full bg-primary-90 dark:bg-primary-30 px-2 py-0.5 text-xs font-medium text-primary-30 dark:text-primary-90"
                                  >
                                    {group.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Details Button */}
                        <div className="border-l-grey-70 flex h-auto shrink-0 items-center border-l px-2">
                          <button
                            className="btn btn-text h-full px-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMemberClick(member.id);
                            }}
                          >
                            <span className="hidden md:block">
                              {t("common.details")}
                            </span>
                            <svg
                              width={20}
                              height={20}
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M9.75781 7.03029C10.1043 6.93744 10.4749 7.03639 10.7285 7.29005L14.6982 11.2901C14.8875 11.4778 14.9941 11.7334 14.9941 12C14.9941 12.2666 14.8875 12.5222 14.6982 12.71L10.6982 16.71C10.512 16.8947 10.2604 16.9989 9.99805 17C9.73224 17.0015 9.4768 16.8972 9.28809 16.71C9.0988 16.5222 8.99219 16.2666 8.99219 16C8.99221 15.7334 9.09879 15.4778 9.28809 15.2901L12.5781 12L9.30762 8.70997C9.05419 8.45635 8.95509 8.08659 9.04785 7.74025C9.14067 7.39386 9.41148 7.12322 9.75781 7.03029Z"
                                fill="#4D4D4D"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  }

                  // Group card
                  const group = item.data as Group;
                  const abbreviation = getGroupAbbreviation(group.name);
                  // Count how many members belong to this group
                  const memberCount = members.filter((m) =>
                    m.groups.some((g) => g.id === group.id)
                  ).length;

                  return (
                    <div
                      key={`group-${group.id}`}
                      className="with-border flex cursor-pointer overflow-hidden rounded-2xl transition-colors hover:bg-grey-95 dark:hover:bg-primary-20"
                      onClick={() => handleGroupClick(group.id)}
                    >
                      <div className="flex w-full items-center gap-3 px-4 py-3">
                        {/* Group Badge */}
                        <div
                          className="relative shrink-0"
                          onMouseEnter={(e) => {
                            const el = e.currentTarget;
                            showGroupTooltip(group.id, el);
                          }}
                          onMouseLeave={hideGroupTooltip}
                        >
                          <div
                            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white"
                            style={{ backgroundColor: getGroupColor(group.name) }}
                          >
                            {abbreviation}
                          </div>
                          <div className="notification-bottom">
                            {memberCount}
                          </div>
                        </div>

                        {/* Info */}
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <p className="truncate font-semibold text-grey-10 dark:text-white cursor-pointer hover:underline">
                            {group.name}
                          </p>
                          <p className="text-sm text-grey-50 dark:text-grey-70">
                            {t("members.memberCount", { count: memberCount })}
                          </p>
                        </div>
                      </div>

                      {/* Details Button */}
                      <div className="border-l-grey-70 flex h-auto shrink-0 items-center border-l px-2">
                        <button
                          className="btn btn-text h-full px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGroupClick(group.id);
                          }}
                        >
                          <span className="hidden md:block">
                            {t("common.details")}
                          </span>
                          <svg
                            width={20}
                            height={20}
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9.75781 7.03029C10.1043 6.93744 10.4749 7.03639 10.7285 7.29005L14.6982 11.2901C14.8875 11.4778 14.9941 11.7334 14.9941 12C14.9941 12.2666 14.8875 12.5222 14.6982 12.71L10.6982 16.71C10.512 16.8947 10.2604 16.9989 9.99805 17C9.73224 17.0015 9.4768 16.8972 9.28809 16.71C9.0988 16.5222 8.99219 16.2666 8.99219 16C8.99221 15.7334 9.09879 15.4778 9.28809 15.2901L12.5781 12L9.30762 8.70997C9.05419 8.45635 8.95509 8.08659 9.04785 7.74025C9.14067 7.39386 9.41148 7.12322 9.75781 7.03029Z"
                              fill="#4D4D4D"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      {groupTooltip && (() => {
        const tooltipGroup = groups.find((g) => g.id === groupTooltip.groupId);
        const groupMembers = members.filter((m) =>
          m.groups.some((g) => g.id === groupTooltip.groupId)
        );
        return createPortal(
          <div
            className="pointer-events-none"
            style={{
              position: 'fixed',
              zIndex: 9999,
              left: groupTooltip.left,
              top: groupTooltip.position === 'top' ? groupTooltip.top - 8 : groupTooltip.top + 8,
              transform: groupTooltip.position === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
            }}
          >
            <div
              className="min-w-max rounded-lg px-3 py-2 shadow-lg"
              style={{ backgroundColor: '#333333' }}
            >
              <p className="text-xs font-semibold text-white mb-1">
                {tooltipGroup?.name ?? 'Group'}
              </p>
              {groupMembers.length > 0
                ? groupMembers.map((m) => (
                    <p key={m.id} className="text-xs" style={{ color: '#cccccc' }}>
                      {m.firstName} {m.lastName}
                    </p>
                  ))
                : <p className="text-xs" style={{ color: '#cccccc' }}>No members</p>
              }
            </div>
            <div
              className="size-0 border-x-[6px] border-x-transparent"
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                ...(groupTooltip.position === 'top'
                  ? { bottom: 0, marginBottom: -6, borderTop: '6px solid #333333' }
                  : { top: 0, marginTop: -6, borderBottom: '6px solid #333333' }
                ),
              }}
            />
          </div>,
          document.body
        );
      })()}
    </section>
  );
};

export default MembersListPage;
